// @flow
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { animated } from 'react-spring/web.cjs';
import { SPACING } from '@tablekit/theme';
import Icon from '@tablekit/icon';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons/faArrowRight';
import LazyLoad from 'react-lazyload';
import type {
  BlokComponentPropsType,
  CardSimpleContentType
} from 'components/types';
import { isValidLink } from 'components/Link';
import { LAZY_LOAD_OFFSET } from 'utils/theme';
import { gridSpan } from 'styled/cssGrid';
import { Card as BaseCard } from '../styled';
import type { CardPropsType } from '../types';
import {
  SMALL_IMAGE_SIZE,
  LARGE_IMAGE_SIZE,
  SimpleContentImage,
  BaseCardGrid,
  BaseSimpleContentArrow,
  BaseSimpleContentTitle,
  BaseSimpleContentBody,
  BaseImagePlacement
} from './static';

const AnimatedCard = animated(styled(BaseCard)`
  position: absolute;
  top: 50%;
  left: 0;
`);

const CardGrid = styled(BaseCardGrid)`
  -ms-grid-columns: 1fr;
  grid-template-columns: 1fr;

  @media (min-width: 576px) {
    -ms-grid-columns: ${SMALL_IMAGE_SIZE}px ${SPACING.L4} 1fr;
    grid-template-columns: ${SMALL_IMAGE_SIZE}px ${SPACING.L4} 1fr;
  }

  @media (min-width: 768px) {
    -ms-grid-columns: 1fr;
    grid-template-columns: 1fr;
  }

  @media (min-width: 1024px) {
    -ms-grid-columns: ${SMALL_IMAGE_SIZE}px ${SPACING.L4} 1fr 1fr;
    grid-template-columns: ${SMALL_IMAGE_SIZE}px ${SPACING.L4} 1fr 1fr;
  }

  @media (min-width: 1280px) {
    -ms-grid-columns: ${LARGE_IMAGE_SIZE}px ${SPACING.L6} 1fr 1fr;
    grid-template-columns: ${LARGE_IMAGE_SIZE}px ${SPACING.L6} 1fr 1fr;
  }
`;

const arrow1ColStyles = css`
  ${gridSpan('column', 1, 1)};
  ${gridSpan('row', 4, 1)};
  align-self: flex-end;
`;

const SimpleContentArrow = styled(BaseSimpleContentArrow)`
  ${arrow1ColStyles};
  @media (min-width: 576px) {
    ${gridSpan('column', 1, 3)};
    ${gridSpan('row', 3, 1)};
  }
  @media (min-width: 768px) {
    ${arrow1ColStyles};
  }
  @media (min-width: 1024px) {
    ${gridSpan('column', 4, 1)};
    ${gridSpan('row', 2, 1)};
    align-self: flex-start;
  }
  @media (min-width: 1280px) {
    ${gridSpan('row', 1, 1)};
  }
`;

const title1ColStyles = css`
  ${gridSpan('column', 1, 1)};
  ${gridSpan('row', 2, 1)};
`;

const SimpleContentTitle = styled(BaseSimpleContentTitle)`
  ${title1ColStyles};
  @media (min-width: 576px) {
    ${gridSpan('column', 3, 1)};
    ${gridSpan('row', 1, 1)};
  }
  @media (min-width: 768px) {
    ${title1ColStyles};
  }
  @media (min-width: 1024px) {
    ${gridSpan('column', 3, 1)};
    ${gridSpan('row', 2, 1)};
  }
  @media (min-width: 1280px) {
    ${gridSpan('row', 1, 1)};
  }
`;

const body1ColStyles = css`
  ${gridSpan('column', 1, 1)};
  ${gridSpan('row', 3, 1)};
  min-height: 120px;
`;

const SimpleContentBody = styled(BaseSimpleContentBody)`
  ${body1ColStyles};
  @media (min-width: 576px) {
    ${gridSpan('column', 1, 3)};
    ${gridSpan('row', 2, 1)};
    min-height: 96px;
  }
  @media (min-width: 768px) {
    ${body1ColStyles};
  }
  @media (min-width: 1024px) {
    ${gridSpan('column', 1, 4)};
    ${gridSpan('row', 4, 1)};
    min-height: 96px;
  }
  @media (min-width: 1280px) {
    ${gridSpan('column', 3, 2)};
    ${gridSpan('row', 2, 1)};
  }
`;

const ImagePlacement = styled(BaseImagePlacement)`
  ${gridSpan('column', 1, 1)};
  ${gridSpan('row', 1, 1)};
  margin-bottom: ${SPACING.L2};

  @media (min-width: 576px) {
    margin-bottom: 0;
  }
  @media (min-width: 768px) {
    margin-bottom: ${SPACING.L2};
  }

  @media (min-width: 1024px) {
    margin-bottom: 0;
    ${gridSpan('row', 1, 3)};
  }
  @media (min-width: 1280px) {
    ${gridSpan('row', 1, 2)};
  }
`;

export function AnimatedCardSimpleContent({
  blok: { body, image, link, link_text: linkText, title },
  ...others
}: BlokComponentPropsType<CardSimpleContentType>) {
  const cardProps: CardPropsType = {
    ...others
  };
  let icon;
  if (isValidLink(link)) {
    cardProps.link = link;
    icon = (
      <SimpleContentArrow>
        <Icon icon={faArrowRight} />
      </SimpleContentArrow>
    );
  }
  return (
    <AnimatedCard {...cardProps}>
      <CardGrid>
        <ImagePlacement>
          {image && (
            <LazyLoad height={SMALL_IMAGE_SIZE} once offset={LAZY_LOAD_OFFSET}>
              <SimpleContentImage
                src={image}
                alt={title || linkText || body || image}
              />
            </LazyLoad>
          )}
        </ImagePlacement>
        <SimpleContentTitle>{title}</SimpleContentTitle>
        {icon}
        <SimpleContentBody>
          <div>{body}</div>
        </SimpleContentBody>
      </CardGrid>
    </AnimatedCard>
  );
}

export const CARD_DISPLAYS = [
  {
    minWidth: 0,
    displayCount: 1,
    height: 360,
    gutter: 6
  },
  {
    minWidth: 768,
    displayCount: 2,
    height: 360,
    gutter: parseInt(SPACING.L5, 10)
  },
  {
    minWidth: 1024,
    displayCount: 2,
    height: 216,
    gutter: parseInt(SPACING.L4, 10)
  },
  {
    minWidth: 1280,
    displayCount: 2,
    height: 232,
    gutter: parseInt(SPACING.L5, 10)
  },
  {
    minWidth: 1440,
    displayCount: 2,
    height: 208,
    gutter: parseInt(SPACING.L5, 10)
  }
];
